import z from "zod";
import { cn } from "@/lib/utils";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { CheckIcon, Loader2Icon } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { Button } from "../ui/button";
import TaskComment from "./taskComment";
import UserAvatar from "../ui/userAvatar";
import InputText from "../common/InputText";
import TaskAttachment from "./taskAttachment";
import TaskDependencies from "./taskDependencies";
import ErrorMessage from "../common/ErrorMessage";
import { Popover, PopoverTrigger, PopoverContent } from "../ui/popover";
import {
  createTaskSchema,
  milestoneTaskSchema,
  noteSchema,
} from "../../../../backend/src/schemas/taskSchema";
import { TASK_MAX_DURATION_DAYS } from "../../../../backend/src/constants/taskLimits";
import { Calendar } from "@/components/ui/calendar";

import { useUser } from "@/hooks/useUser";
import useTaskQuery from "@/api/query/useTaskQuery";
import dateFormater from "@/helperFuntions/dateFormater";
import useTaskAddMembersMutation from "@/api/mutation/useTaskAddMember";
import useCreateTaskMutation, {
  Task,
} from "@/api/mutation/useTaskCreateMutation";
import useUpdateTaskMutation from "@/api/mutation/useTaskUpdateMutation";
import useRemoveTaskMemberMutation from "@/api/mutation/useTaskRemoveMember";
import { UserOrganisationType } from "@/api/query/useOrganisationDetailsQuery";
import useTaskAttechmentAddMutation from "@/api/mutation/useTaskAttechmentAddMutation";
import useTaskAddUpdateMilestoneMutation from "@/api/mutation/useTaskAddUpdateMilestone";
import useAllTaskQuery from "@/api/query/useAllTaskQuery";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/api/query/querykeys";

import PlusSvg from "../../assets/v2/svg/Squareplus.svg";
import Edit from "../../assets/v2/svg/Pencil.svg";
import EditSquare from "../../assets/v2/svg/edit.svg";
import TrashCan from "../../assets/v2/svg/RedTrash.svg";
import YallowInfoIcon from "@/assets/svg/YallowInfoIcon.svg";
import Close from "../../assets/v2/svg/RedCross.svg";
import PapperClip from "../../assets/v2/svg/paperclip.svg";
import InfoCircle from "../../assets/png/information.png";
import BackIcon from "../../assets/v2/svg/enterIcon.svg";
import CalendarIcon from "../../assets/v2/svg/CalendarIcon.svg";
import ScaleSvg from "../../assets/svg/ScaleSvg.svg";
import DesktopSvg from "../../assets/svg/Desktop.svg";
import DropboxSvg from "../../assets/svg/Dropbox.svg";
import GoogleSvg from "../../assets/svg/google.svg";
import InputNumber from "@/components/common/InputNumber";
import TaskHistory from "./taskHistory";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  TaskDependenciesEnumValue,
  TaskStatusEnumValue,
  ProjectStatusEnumValue,
  UserRoleEnumValue,
} from "@backend/src/schemas/enums";
import useTaskStatusUpdateMutation from "@/api/mutation/useTaskStatusUpdateMutation";
import { Slider } from "../ui/slider";
import Loader from "../common/Loader";
import useAllKanbanColumnQuery from "@/api/query/useAllKanbanColumn";
import Dialog from "../common/Dialog";
import ProjectStatusEditBlockedDialog from "@/components/project/ProjectStatusEditBlockedDialog";
import useRemoveTaskDependenciesMutation from "@/api/mutation/useTaskRemoveDependencies";

import RedFlag from "@/assets/svg/RedFlag.svg";
import OrangeFlag from "@/assets/svg/OrangeFlag.svg";
import GreenFlag from "@/assets/svg/GreenFlag.svg";
// import PercentageCircle from "../shared/PercentageCircle";
import ParentTaskIcon from "@/assets/v2/svg/listIcon.svg";
import NoramlPlus from "@/assets/v2/svg/PlusSecondary.svg";
import RedCross from "@/assets/v2/svg/RedCross.svg";
import TickCircle from "@/assets/v2/svg/tick-circle.svg";
import { errorConfig } from "@/lib/toastError";
import { generateUniqueTaskName } from "../shared/taskNameGenrater";
import { useTranslation } from "react-i18next";
import { OrgListOfNonWorkingDaysEnum } from "@backend/src/schemas/organisationSchema";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import { debounce } from "lodash";
import OverlaySlider from "../SlidingOverlay";
import "./task.css";
import {
  appUrl,
  msalClientId,
  googleApiKey,
  googleCredentialsClientId,
} from "@/Environment";
import useSharePointAttechmentAddMutation from "@/api/mutation/useSharePointAttechmentMutaion";
import useProjectDetail from "@/api/query/useProjectDetailQuery";
import MemberAssignmentItem from "./taskAssignmentItem";
import useUpdateTaskMemberMutation from "@/api/mutation/useAssignTaskMemeberUpdateMutation";
import { Link } from "react-router-dom";
import useDebounce from "@/hooks/useDebounce";
import useRemoveTaskMutation from "@/api/mutation/useTaskRemove";
import { useTaskLinkAttachmentAddMutation } from "@/api/mutation/useTaskLinkAttachmentAddMutation";
import useUpdateTaskParentMutation from "@/api/mutation/useTaskParentUpdateMutation";
import useAddNoteMutation from "@/api/mutation/useAddTaskNotesMutation";
import useRemoveNoteMutation from "@/api/mutation/useRemoveNotesMutation";
import useUpdateNoteMutation from "@/api/mutation/useUpdateTaskNotesMutation";
import Notes from "@/assets/v2/svg/Note.svg";
import { isAxiosError } from "axios";
import {
  canManageProjectTasksAndContent,
  normalizeProjectScope,
} from "@/utils/projectRoleAccess";
declare global {
  interface Window {
    OneDrive: any;
  }
}
type Props = {
  projectId: string | undefined;
  taskId?: string | undefined;
  close: (data: string) => void;
  initialValues?: { startDate: Date | undefined; taskId?: string };
  createSubtask?: string;
};

const toDateTimestamp = (value: Date | string | undefined): number | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

function TaskSubTaskForm(props: Props) {
  const queryClient = useQueryClient();
  const tempIdRef = useRef("");
  const { t } = useTranslation();
  const currantUser = useUser();
  const [subTask, setSubtask] = useState("");
  const [errorMessage, setErrorMesage] = useState("");
  const [updateDate, setUpdateDate] = useState<Date>();
  const fileInput = useRef<HTMLInputElement>(null);
  const [taskNameField, setTaskNameField] = useState(false);
  const [taskDurationField, setTaskDurationField] = useState(false);
  const [taskId, setTaskId] = useState<string>(props.taskId ?? "");
  const [member, setMambers] = useState<UserOrganisationType["user"][]>([]);
  const [attachmentUploading, setAttachmentUploading] =
    useState<boolean>(false);
  const [subTaskFieldShow, setSubTaskFieldShow] = useState<boolean>(false);
  const taskQuery = useTaskQuery(taskId, !!taskId);
  const taskUpdateMutation = useUpdateTaskMutation(taskId);
  const updateTaskParentMutation = useUpdateTaskParentMutation();
  const taskRemoveMembersMutation = useRemoveTaskMemberMutation();
  const taskAddMembersMutation = useTaskAddMembersMutation(taskId);
  // let tasks =
  //   taskQuery.data?.data.data && taskId ? taskQuery.data?.data.data : undefined;
  const [task, setTask] = useState(
    taskQuery.data?.data.data && taskId ? taskQuery.data?.data.data : undefined,
  );
  const [slider, setSlider] = useState(Number(task?.completionPecentage));
  const taskAttachmentAddMutation = useTaskAttechmentAddMutation(taskId);
  const [invalidDate, setInvalidDate] = useState<boolean>(false);
  const removeTaskDependenciesMutation = useRemoveTaskDependenciesMutation();
  const taskCreateMutation = useCreateTaskMutation(
    props.projectId,
    props.createSubtask ? props.createSubtask : taskId ? taskId : "",
  );
  const sharePointAttechmentAddMutation =
    useSharePointAttechmentAddMutation(taskId);
  const [endDate, setEndDate] = useState<Date>();

  // Link Attachment State
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkName, setLinkName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkType, setLinkType] = useState<"WEB" | "LOCAL">("WEB");
  const { mutate: addLink, isPending: isLinkUploading } =
    useTaskLinkAttachmentAddMutation(taskId);

  const taskAddUpdateMilestoneMutation =
    useTaskAddUpdateMilestoneMutation(taskId);
  const allKanbanColumn = useAllKanbanColumnQuery(props.projectId);
  useEffect(() => {
    if (taskQuery.status == "success") {
      setSlider(Number(taskQuery.data?.data.data?.completionPecentage));
    }
    setTask(taskQuery.data?.data.data);
  }, [taskQuery.data?.data.data, taskQuery.status]);
  // const [submitbyButton, setSubmitbyButton] = useState(false);

  const user = useUser();
  const [parentTask, setParentTask] = useState<string>();
  const allTask = useAllTaskQuery(props.projectId, !!props.projectId);
  const ProjectDetail = useProjectDetail(props.projectId, !!props.projectId);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const projectStatus = ProjectDetail.data?.data.data.status;
  const isProjectEditBlocked =
    projectStatus === ProjectStatusEnumValue.CANCELLED ||
    projectStatus === ProjectStatusEnumValue.ON_HOLD ||
    projectStatus === ProjectStatusEnumValue.COMPLETED ||
    projectStatus === ProjectStatusEnumValue.CLOSED;

  const [isProjectEditBlockedDialogOpen, setIsProjectEditBlockedDialogOpen] =
    useState(false);
  const [blockedDialogTriggered, setBlockedDialogTriggered] = useState(false);

  useEffect(() => {
    if (isProjectEditBlocked && !blockedDialogTriggered) {
      setIsProjectEditBlockedDialogOpen(true);
      setBlockedDialogTriggered(true);
    }
  }, [isProjectEditBlocked, blockedDialogTriggered]);

  useEffect(() => {
    setTask(taskQuery.data?.data.data);
  }, [taskQuery.data]);

  useEffect(() => {
    if (taskId && task) {
      taskFormik.setValues({
        taskName: task.taskName,
        taskDescription: task.taskDescription ?? "",
        startDate: task.startDate ? new Date(task.startDate) : new Date(),
        duration: task.duration ? Number(task.duration) : 1,
      });
      milestoneFormik.setValues({
        milestoneIndicator: task.milestoneIndicator,
      });
      setMambers(
        task.assignedUsers.map((u) => {
          return u.user;
        }),
      );
    }

    setParentTask(
      allTask.data?.data.data.find(
        (t) => t.taskId == (task?.parentTaskId ?? props.createSubtask),
      )?.taskName,
    );

    if (
      allKanbanColumn.data?.data.data &&
      allKanbanColumn.data?.data.data?.length <= 1 &&
      !task
    ) {
      const id = allKanbanColumn.data.data.data[0]?.kanbanColumnId;
      const updatedValues = { ...taskFormik.values, kanbanColumnId: id };
      taskFormik.setValues(updatedValues);
    }
    if (ProjectDetail) {
      setStartDate(
        new Date(
          ProjectDetail?.data?.data.data.startDate ?? new Date(),
        ),
      );
    }
    const endDate = task ? task.endDate : new Date();
    setEndDate(new Date(endDate));

    // Log Formik validation errors for debugging
    if (Object.keys(taskFormik.errors).length > 0) {
      console.warn("Formik Validation Errors:", taskFormik.errors);
    }
  }, [task, taskId]);

  const allowed = () => {
    const project = ProjectDetail.data?.data.data ?? undefined;
    const me = user.user;
    if (!taskId) {
      return true;
    }
    if (!me || !project) {
      return false;
    }
    if (
      canManageProjectTasksAndContent(
        me,
        me.userOrganisation[0]?.role,
        project.assignedUsers,
        normalizeProjectScope(project),
      )
    ) {
      return true;
    }
    if (
      project.assignedUsers.find(
        (u) =>
          u.user.userId == me.userId &&
          u.projectRole == UserRoleEnumValue.COLLABORATOR,
      )
    ) {
      return false;
    }
    if (
      task?.assignedUsers.find((u) => u.user.userId === me.userId) ||
      task?.createdByUserId == me.userId
    ) {
      return true;
    }
    return false;
  };

  const guardProjectEditBlocked = () => {
    if (!isProjectEditBlocked) return false;
    setIsProjectEditBlockedDialogOpen(true);
    setBlockedDialogTriggered(true);
    return true;
  };

  const taskFormValidationSchema = useMemo(
    () =>
      createTaskSchema.superRefine((vals, ctx) => {
        const ps = ProjectDetail?.data?.data?.data?.startDate;
        if (!ps) return;
        const sd =
          vals.startDate instanceof Date
            ? vals.startDate
            : new Date(vals.startDate as unknown as string);
        if (Number.isNaN(sd.getTime())) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid start date.",
            path: ["startDate"],
          });
          return;
        }

        // Skip validation when start date is unchanged (e.g. Jira imports may predate project start)
        if (
          task?.startDate &&
          toDateTimestamp(vals.startDate) === toDateTimestamp(task.startDate)
        ) {
          return;
        }

        const projectDay = Date.UTC(
          new Date(ps).getFullYear(),
          new Date(ps).getMonth(),
          new Date(ps).getDate(),
        );
        const taskDay = Date.UTC(
          sd.getFullYear(),
          sd.getMonth(),
          sd.getDate(),
        );
        if (taskDay < projectDay) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Task start cannot be before the project start date.",
            path: ["startDate"],
          });
        }
      }),
    [ProjectDetail?.data?.data?.data?.startDate, task?.startDate],
  );

  const taskFormik = useFormik<z.infer<typeof createTaskSchema>>({
    initialValues: {
      taskName: "",
      taskDescription: "",
      startDate: new Date(),
      duration: 1 as unknown as number,
    },
    validationSchema: toFormikValidationSchema(taskFormValidationSchema),
    onSubmit: (values, helper) => {
      if (guardProjectEditBlocked()) return;
      if (taskId || tempIdRef.current) {
        const startDateChanged =
          toDateTimestamp(values.startDate) !== toDateTimestamp(task?.startDate);
        const durationChanged =
          Number(values.duration) !== Number(task?.duration ?? 0);
        const taskNameChanged = values.taskName !== task?.taskName;
        const descriptionChanged =
          values.taskDescription !== (task?.taskDescription ?? "");

        if (
          !startDateChanged &&
          !durationChanged &&
          !taskNameChanged &&
          !descriptionChanged
        ) {
          return;
        }

        const data: Partial<z.infer<typeof createTaskSchema>> = {};
        if (taskNameChanged) data.taskName = values.taskName;
        if (descriptionChanged) data.taskDescription = values.taskDescription;
        if (startDateChanged) data.startDate = values.startDate;
        if (durationChanged) data.duration = values.duration;

        taskUpdateMutation.mutate(data, {
            onSuccess() {
              // toast.success(data.data.message);
              // if (submitbyButton) {
              //   props.close();
              // }
              refetch();
            },
            onError(error) {
              if (
                error.response?.data.message?.includes(
                  "Would you like to remove the dependency?",
                )
              ) {
                setErrorMesage(error.response?.data.message);
                setInvalidDate(true);
                refetch();
              } else {
                refetch();
                toast.error(
                  error.response?.data.message == "Unauthorized"
                    ? t(
                      "You are not authorized to edit tasks which are not assigned to you",
                    )
                    : errorKeyFormate(error.response?.data.message + ""),
                  errorConfig,
                );
              }
            },
          });
      } else {
        if (props.initialValues?.startDate) {
          values.startDate = new Date(props.initialValues?.startDate);
          values.startDate.setDate(values.startDate.getDate() + 1);
        }
        values.startDate.setUTCHours(0, 0, 0, 0);
        taskCreateMutation.mutate(values, {
          onSuccess(data) {
            refetch();
            setTaskId(data.data.data.taskId);
            // toast.success(data.data.message);
            // projects.refetch();
          },
          onError(error) {
            refetch();
            if (isAxiosError(error)) {
              if (
                error.response?.status === 400 &&
                error.response.data?.errors &&
                Array.isArray(error.response?.data.errors)
              ) {
                error.response.data.errors.forEach((item) => {
                  helper.setFieldError(item.path[0], item.message);
                });
              }
              if (!Array.isArray(error.response?.data.errors)) {
                toast.error(
                  errorKeyFormate(error.response?.data.message + ""),
                  errorConfig
                );
              }
            }
          },
        });
      }
    },
  });

  useEffect(() => {
    if (!taskId) {
      const parentTaskDate = allTask.data?.data?.data.find(
        (t) => t.taskId === (task?.parentTaskId ?? props.createSubtask),
      )?.startDate;

      const initialStartDate = props.initialValues?.startDate;
      const project = ProjectDetail?.data?.data?.data
      const projectStartDate = project?.startDate;

      let date = new Date();
      if (initialStartDate) {
        date = new Date(initialStartDate);
      } else if (projectStartDate) {
        const projectDate = new Date(projectStartDate);
        date = projectDate >= new Date() ? projectDate : new Date();
      }

      if (parentTaskDate) {
        date = new Date(parentTaskDate);
      }

      taskFormik.setFieldValue("startDate", date);
    }
  }, [
    taskId,
    task?.parentTaskId,
    props.createSubtask,
    props.initialValues?.startDate,
    props.projectId,
    ProjectDetail?.data?.data?.data,
    allTask.data?.data?.data,
  ]);

  const milestoneFormik = useFormik<z.infer<typeof milestoneTaskSchema>>({
    initialValues: {
      milestoneIndicator: false,
    },
    validationSchema: toFormikValidationSchema(milestoneTaskSchema),
    onSubmit: (values) => {
      milestoneSubmit(values);
    },
  });
  const milestoneSubmit = async (
    values: z.infer<typeof milestoneTaskSchema>,
  ) => {
    if (guardProjectEditBlocked()) return;
    if (!taskId && !taskCreateMutation.isPending) {
      await createTaskWithValue()?.then(() => {
        taskAddUpdateMilestoneMutation.mutate(
          { ...values, taskId: tempIdRef.current },
          {
            onSuccess() {
              refetch();
            },
            onError(error) {
              toast.error(
                errorKeyFormate(error.response?.data.message + ""),
                errorConfig,
              );
            },
          },
        );
      });
    }
    if (taskId) {
      taskAddUpdateMilestoneMutation.mutate(
        { ...values, taskId: tempIdRef.current },
        {
          onSuccess() {
            refetch();
          },
          onError(error) {
            toast.error(
              errorKeyFormate(error.response?.data.message + ""),
              errorConfig,
            );
          },
        },
      );
    }
    // Ensuring taskId is set before mutating
  };
  const milestoneHandlechange = (e: boolean) => {
    milestoneFormik.setFieldValue("milestoneIndicator", e);
    if (!e) {
      milestoneSubmit({ milestoneIndicator: false });
    }
  };

  const createSubTask = async () => {
    if (guardProjectEditBlocked()) return;
    let value: {
      taskName: string;
      taskDescription: string;
      startDate: Date;
      duration: number;
      assginedToUserId: string[];
      milestoneIndicator: boolean;
      tempId?: string;
    } = {
      taskName: subTask,
      taskDescription: "",
      startDate: task?.startDate ?? new Date(),
      duration: 1,
      assginedToUserId: [currantUser.user?.userId ?? ""],
      milestoneIndicator: false,
    };
    if (!taskId) {
      await createTaskWithValue();
      value = { ...value, tempId: tempIdRef.current };
    }
    taskCreateMutation.mutate(value, {
      onSuccess() {
        refetch();
        setSubtask("");
        // toast.success(data.data.message);
        setSubTaskFieldShow((prev) => (prev = !prev));
      },
      onError(error) {
        toast.error(
          errorKeyFormate(errorKeyFormate(error.response?.data.message + "")),
          errorConfig,
        );
      },
    });
  };

  const refetch = () => {
    const activeId = taskId || tempIdRef.current;
    if (activeId) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.taskById, activeId] });
    }
  };

  const handleToggleMember = useCallback(
    debounce((data: UserOrganisationType, workingHours?: number) => {
      if (
        !taskQuery.isFetching &&
        !taskQuery.isRefetching &&
        !taskAddMembersMutation.isPending &&
        !taskRemoveMembersMutation.isPending
      ) {
        const userExists = task?.assignedUsers?.some(
          (u) => u.user.userId == data.user.userId,
        );

        if (userExists) {
          const idToRemove =
            task?.assignedUsers.find((id) => id.user.userId == data.user.userId)
              ?.taskAssignUsersId ?? "";

          removeMembers(idToRemove);
        } else {
          submitMembers(data, workingHours ?? 8);
        }
      }
    }, 500),
    [
      taskAddMembersMutation,
      taskRemoveMembersMutation,
      taskQuery,
      task,
    ],
  );

  const submitMembers = async (
    user: UserOrganisationType,
    workingHours: number,
  ) => {
    if (guardProjectEditBlocked()) return;
    if (!taskId) {
      await createTaskWithValue();
    }
    if (user) {
      taskAddMembersMutation.mutate(
        {
          assginedToUserId: user.user.userId,
          workingHours: workingHours,
          taskId: tempIdRef.current ? tempIdRef.current : taskId,
        },
        {
          onSuccess() {
            refetch();
            setMambers((prev) => [...prev, user.user]);
            ProjectDetail.refetch();
          },
          onError(error) {
            toast.error(
              errorKeyFormate(error.response?.data.message + ""),
              errorConfig,
            );
          },
        },
      );
    }
  };

  const removeMembers = (id: string) => {
    if (guardProjectEditBlocked()) return;
    taskRemoveMembersMutation.mutate(id, {
      onSuccess() {
        refetch();
        // toast.success(data.data.message);
        ProjectDetail.refetch();
      },
      onError(error) {
        toast.error(
          errorKeyFormate(error.response?.data.message + ""),
          errorConfig,
        );
      },
    });
  };
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(
    null,
  );
  const [showConfirmEdit, setShowConfirmEdit] = useState<{
    taskName: string;
    taskId: string;
  } | null>(null);
  const removeTaskMutation = useRemoveTaskMutation();
  const removeTask = () => {
    if (showConfirmDelete) {
      removeTaskMutation.mutate(showConfirmDelete, {
        onSuccess(data) {
          setShowConfirmDelete(null);
          taskQuery.refetch();
          toast.success(data.data.message);
        },
        onError(error) {
          toast.error(
            errorKeyFormate(error.response?.data.message + ""),
            errorConfig,
          );
        },
      });
    }
  };
  const nonWorkingDays = () => {
    const nWd =
      user?.user?.userOrganisation.map(
        (org) => org.organisation.nonWorkingDays,
      ) || [];
    const mapDayToNumber = (day: OrgListOfNonWorkingDaysEnum) => {
      switch (day) {
        case "SUN":
          return 0;
        case "MON":
          return 1;
        case "TUE":
          return 2;
        case "WED":
          return 3;
        case "THU":
          return 4;
        case "FRI":
          return 5;
        case "SAT":
          return 6;
        default:
          return 0;
      }
    };
    const result: number[] = [];
    nWd.forEach((days) => {
      days.forEach((day) => {
        result.push(mapDayToNumber(day));
      });
    });
    return result;
  };
  const fileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (guardProjectEditBlocked()) return;
    if (!taskId) {
      await createTaskWithValue();
    }
    if ((taskId || tempIdRef.current) && e.target.files?.length) {
      setAttachmentUploading(true);
      const formData = new FormData();
      for (let i = 0; i < e.target.files.length; ++i) {
        formData.append("taskAttachment", e.target.files[i]);
      }
      taskAttachmentAddMutation.mutate(
        { FormData: formData, tempId: tempIdRef.current },
        {
          onSuccess() {
            refetch();
            // toast.success(data.data.message);
            setAttachmentUploading(false);
          },
          onError(error) {
            toast.error(
              errorKeyFormate(error.response?.data.message + ""),
              errorConfig,
            );
            setAttachmentUploading(false);
          },
        },
      );
    }
    setOpen(false);
  };
  const [progressError, setProgressError] = useState("");
  const [scheduledError, setScheduledError] = useState(false);
  const onProgressUpdate = async (e: number[]) => {
    if (guardProjectEditBlocked()) return;
    if (!taskId) {
      await createTaskWithValue();
    }
    const val = e[0];

    // If task starts in the future, block the update
    const today = new Date();
    const todayDateOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    const startDate = new Date(task?.startDate ?? "");
    const startDateOnly = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
    );

    // If task starts in the future, block the update
    if (startDateOnly > todayDateOnly) {
      console.log("call");
      setScheduledError(true);

      return;
    }
    if (
      Number(val) !== Number(task?.completionPecentage) &&
      !taskCreateMutation.isPending
    ) {
      if (Number(val) > 100 || Number(val) < 0) {
        setProgressError("Progress must be within the range of 0 to 100.");
      } else {
        setProgressError("");
        taskUpdateMutation.mutate(
          {
            completionPecentage: Number(val) ?? 0,
            kanbanColumnId: "",
            id: tempIdRef.current,
          },
          {
            onSuccess() {
              refetch();
            },
            onError(error) {
              toast.error(
                error.response?.data.message == "Unauthorized"
                  ? t(
                    "You are not authorized to edit tasks which are not assigned to you",
                  )
                  : errorKeyFormate(error.response?.data.message + ""),
                errorConfig,
              );
            },
          },
        );
      }
    }
  };
  const taskStatusUpdateMutation = useTaskStatusUpdateMutation(taskId);
  const updateStatus = async (status: string) => {
    if (guardProjectEditBlocked()) return;
    const today = new Date();
    const todayDateOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    const startDate = new Date(task?.startDate ?? "");
    const startDateOnly = new Date(
      startDate.getFullYear(),
      startDate.getMonth(),
      startDate.getDate(),
    );

    // If task starts in the future, block the update
    if (startDateOnly > todayDateOnly) {
      setScheduledError(true);

      return;
    }
    if (!taskId) {
      await createTaskWithValue();
    }

    taskStatusUpdateMutation.mutate(
      { status, taskId: tempIdRef.current },
      {
        onSuccess() {
          refetch();
        },
        onError(error) {
          toast.error(
            error.response?.data.message == "Unauthorized"
              ? t(
                "You are not authorized to edit tasks which are not assigned to you",
              )
              : errorKeyFormate(error.response?.data.message + ""),
            errorConfig,
          );
        },
      },
    );
  };
  const taskStatus = () => {
    const removeStatus = [TaskStatusEnumValue.IN_PROGRESS];
    if (task?.milestoneIndicator) {
      return Object.keys(TaskStatusEnumValue).filter(
        (e) => !removeStatus?.some((r) => e === r),
      );
    } else {
      return Object.keys(TaskStatusEnumValue);
    }
  };
  const close = () => {
    setTask(undefined);
    if (taskId) {
      allTask.refetch();
    }
    props.close(taskId);
  };
  const deleteDependenciesId = () => {
    if (guardProjectEditBlocked()) return;
    const taskDependenciesId = taskQuery?.data?.data.data.dependencies?.find(
      (item) =>
        item.dependentTaskId === taskQuery.data?.data.data.taskId &&
        item.dependentType === TaskDependenciesEnumValue.PREDECESSORS,
    )?.taskDependenciesId;
    if (taskDependenciesId) {
      removeTaskDependenciesMutation.mutate(taskDependenciesId, {
        onSuccess(data) {
          refetch();
          taskUpdateMutation.mutate(
            { startDate: updateDate },
            {
              onSuccess() {
                refetch();
              },
            },
          );
          toast.success(data.data.message);
          setInvalidDate(false);
        },
        onError(error) {
          toast.error(
            error.response?.data.message == "Unauthorized"
              ? t(
                "You are not authorized to edit tasks which are not assigned to you",
              )
              : errorKeyFormate(error.response?.data.message + ""),
            errorConfig,
          );
          setInvalidDate(false);
        },
      });
    }
  };
  const [showAllSubTitle, setShowAllSubTitle] = useState(false);

  const createTaskWithValue = (value?: {
    startDate?: Date;
    taskDescription?: string;
    duration?: number;
  }) => {
    if (guardProjectEditBlocked()) return Promise.resolve();
    if (taskCreateMutation.isPending && !taskId) {
      return;
    }
    const project = ProjectDetail?.data?.data?.data

    const data = {
      taskName: generateUniqueTaskName(
        allTask.data?.data.data ?? [],
        project?.projectName ?? "",
      ),
      taskDescription: value?.taskDescription,
      startDate: UTCDateConverter(
        value?.startDate
          ? new Date(value.startDate)
          : taskFormik.values.startDate,
      ),
      duration: value?.duration ?? 1,
    };
    return new Promise<void>((resolve, reject) => {
      taskCreateMutation.mutate(data, {
        onSuccess(res) {
          setTaskId(res.data.data.taskId);
          setTask(res.data.data);
          tempIdRef.current = res.data.data.taskId;
          resolve();
        },
        onError(error) {
          refetch();
          toast.error(
            errorKeyFormate(error.response?.data.message + ""),
            errorConfig,
          );
          reject(error);
        },
      });
    });
  };

  const holiday = user.user?.userOrganisation[0].organisation.orgHolidays?.map(
    (t) => new Date(t.holidayStartDate),
  );

  const getSelectedDate = useCallback(() => {
    const date = taskFormik.values.startDate
      ? new Date(taskFormik.values.startDate)
      : ProjectDetail?.data?.data?.data?.startDate
        ? new Date(ProjectDetail.data.data.data.startDate)
        : new Date(startDate);
    if (isNaN(date.getTime())) {
      return new Date(); // Fallback to current date if invalid
    }
    date.setHours(0, 0, 0, 0);
    return date;
  }, [startDate, taskFormik.values.startDate, ProjectDetail?.data?.data?.data?.startDate]);
  const UTCDateConverter = (date: Date) => {
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0),
    );
  };
  function calculateDurations(startDate: Date, endDate: Date): number {
    const start = UTCDateConverter(new Date(startDate));
    const end = UTCDateConverter(new Date(endDate));

    // Include end date in calculation (add 1 day)
    end.setDate(end.getDate() + 1);

    const excludedDays = nonWorkingDays(); // e.g., [0, 6] for SUN, SAT

    const holidays =
      user?.user?.userOrganisation?.[0]?.organisation?.orgHolidays?.map((h) =>
        dateFormater(new Date(h.holidayStartDate)),
      ) ?? [];

    let duration = 0;

    for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
      const day = d.getDay();
      const formattedDate = d.toISOString().split("T")[0];

      if (
        !excludedDays.includes(day) &&
        !holidays.includes(dateFormater(new Date(formattedDate)))
      ) {
        duration++;
      }
    }

    return duration;
  }

  // components/SharePointPicker.tsx

  const openPicker = () => {
    const odOptions = {
      clientId: msalClientId, // From Azure Portal
      action: "download", // or "query", "share" etc.
      multiSelect: true,
      oauth2AllowIdTokenImplicitFlow: true,
      advanced: {
        redirectUri: appUrl, // Must match Azure app registration
        // optional: restrict to business accounts
        openInNewWindow: true,
        queryParameters: "select=id,name,size,file,webUrl",
      },
      cache: {
        cacheLocation: "localStorage",
      },
      success: async function (files: any) {
        if (guardProjectEditBlocked()) {
          setOpen(false);
          return;
        }
        if (!taskId) {
          await createTaskWithValue();
        }
        const data = {
          file: files.value.map((f: any) => {
            return {
              name: f.name,
              url: f.webUrl,
            };
          }),
          tempId: tempIdRef.current,
        };
        sharePointAttechmentAddMutation.mutate(data, {
          onSuccess() {
            refetch();
            // toast.success(data.data.message);
            setAttachmentUploading(false);
          },
          onError(error) {
            toast.error(
              errorKeyFormate(error.response?.data.message + ""),
              errorConfig,
            );
            setAttachmentUploading(false);
          },
        });
        setOpen(false);
      },
      cancel: function () {
        console.log("User cancelled the picker.");
      },
      error: function (e: any) {
        console.error("Picker error:", e);
      },
    };
    window.OneDrive.open(odOptions);
  };
  const openGoogleDrivePicker = () => {
    const handlePickerCallback = async (data: any) => {
      if (data.action === "picked") {
        if (guardProjectEditBlocked()) {
          setAttachmentUploading(false);
          setOpen(false);
          return;
        }
        if (!taskId) {
          await createTaskWithValue();
        }
        const files = data.docs.map((doc: any) => ({
          name: doc.name,
          url: doc.url,
        }));
        const attachmentData = {
          file: files,
          tempId: tempIdRef.current,
        };
        sharePointAttechmentAddMutation.mutate(attachmentData, {
          onSuccess() {
            refetch();
            setAttachmentUploading(false);
          },
          onError(error) {
            toast.error(
              errorKeyFormate(error.response?.data.message + ""),
              errorConfig,
            );
            setAttachmentUploading(false);
          },
        });
      }
    };

    const initPicker = (token: string) => {
      const picker = new (window as any).google.picker.PickerBuilder()
        .addView((window as any).google.picker.ViewId.DOCS)
        .setOAuthToken(token)
        .setDeveloperKey(googleApiKey)
        .setCallback(handlePickerCallback)
        .build();
      picker.setVisible(true);
    };

    const tokenClient = (window as any).google.accounts.oauth2.initTokenClient({
      client_id: googleCredentialsClientId,
      scope: "https://www.googleapis.com/auth/drive.file",
      callback: async (response: any) => {
        if (response.access_token) {
          (window as any).gapi.load("picker", () =>
            initPicker(response.access_token),
          );
        }
      },
    });

    tokenClient.requestAccessToken();
  };

  const [open, setOpen] = useState(false);
  const [taskMember, setTaskMember] = useState<UserOrganisationType[]>([]);
  const setMember = () => {
    const assignedUsers = ProjectDetail.data?.data.data.assignedUsers ?? [];

    const filtered = assignedUsers.filter(
      (t) =>
        t.projectRole !== UserRoleEnumValue.COLLABORATOR
    );

    setTaskMember(filtered as any);
  };
  useEffect(() => {
    setMember();
  }, [
    ProjectDetail.data?.data.data.assignedUsers,
  ]);
  const updateTaskMemberMutation = useUpdateTaskMemberMutation();
  const handleUpdateMemberAssignment = (
    member: UserOrganisationType,
    workingHours: number,
  ) => {
    if (guardProjectEditBlocked()) return;
    if (!taskId) return; // Can't update if there's no task

    // We reuse the 'add member' mutation. The backend should handle this as an 'upsert' (update if exists, else add).
    // This is a common pattern. If your backend requires a different endpoint/method for updates, you'd create a new mutation hook.
    const assignId =
      task?.assignedUsers.find((t) => t.user.userId == member.user.userId)
        ?.taskAssignUsersId ?? "";
    updateTaskMemberMutation.mutate(
      {
        assginedToUserId: assignId,
        workingHours: workingHours,
      },
      {
        onSuccess: () => {
          // No toast needed for a quiet update, just refetch to confirm.
          refetch();
          ProjectDetail.refetch();
        },
        onError: (error) => {
          toast.error(
            errorKeyFormate(error.response?.data.message + ""),
            errorConfig,
          );
        },
      },
    );
  };
  const [totalWorkHoursTask, setTotalWorkHoursTask] = useState(0);
  useEffect(() => {
    let total = 0;
    task?.assignedUsers?.forEach((e) => {
      total += e.workingHours;
    });

    setTotalWorkHoursTask(Math.max(total, 0));
  }, [task]);

  const [searchString, setSearchString] = useDebounce("", 400);
  const [filteredUser, setFilteredUser] = useState<UserOrganisationType[]>([]);

  useEffect(() => {
    const tempData: UserOrganisationType[] = [];
    if (searchString) {
      taskMember?.forEach((element) => {
        const searchStringLower = searchString?.toLowerCase();
        const userFirstNameLower = element.user.firstName?.toLowerCase();
        const userLastNameLower = element.user.lastName?.toLowerCase();
        const userEmail = element.user.email?.toLowerCase();
        if (
          (searchStringLower !== undefined &&
            userFirstNameLower?.includes(searchStringLower)) ||
          userLastNameLower?.includes(searchStringLower) ||
          userEmail?.includes(searchStringLower)
        ) {
          tempData.push(element);
        }
      });
      setFilteredUser(tempData ?? []);
    } else {
      setFilteredUser(taskMember);
    }
  }, [searchString, taskMember]);

  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSubtask(value);
    if (value.length > 0) {
      const matches =
        allTask.data?.data.data?.filter(
          (task) =>
            task.taskName.toLowerCase().includes(value.toLowerCase()) &&
            task.taskId !== taskId,
        ) ?? [];
      setFilteredTasks(matches ?? []);
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  };

  const handleSelectTask = async (filterTask: Task) => {
    if (!taskId) {
      await createTaskWithValue();
    }
    updateTaskParentMutation.mutate(
      {
        parentTaskId: tempIdRef.current ? tempIdRef.current : task?.taskId,
        id: filterTask.taskId,
      },
      {
        onSuccess() {
          taskQuery.refetch();
        },
      },
    );

    setShowDropdown(false);
    setSubTaskFieldShow(false);
  };
  const createSubtasksOption = useCallback((): Task[] => {
    if (!allTask) return [];
    const allTasks = filteredTasks;
    return allTasks.filter((t) => {
      if (t.taskId === task?.taskId) return false;
      if (task?.taskId == t.parentTaskId) return false;
      return true;
    });
  }, [filteredTasks, task]);
  const isWebUrl = (url: string) => /^(https?:)?\/\/.+/i.test(url);

  const isLocalPath = (url: string) =>
    /^(\\\\[^\\]+\\[^\\]+|\/(?!\/)|\.\/|\.\.\/|file:\/\/)/i.test(url);

  const [showAddField, setShowAddField] = useState(false);
  const useAddNotesMutation = useAddNoteMutation(taskId);
  const [editNotes, setEditNotes] = useState("");
  const useUpdateNotesMutation = useUpdateNoteMutation(editNotes);

  const noteFormik = useFormik<z.infer<typeof noteSchema>>({
    initialValues: {
      note: "",
    },
    validationSchema: toFormikValidationSchema(noteSchema),
    onSubmit: async (values) => {
      if (editNotes) {
        useUpdateNotesMutation.mutate(
          { ...values },
          {
            onSuccess(data) {
              noteFormik.resetForm();
              toast.success(data.data.message);
              setTask((prev) => {
                if (!prev) return prev;

                return {
                  ...prev,
                  taskNotes: [...(prev.taskNotes?.map(n => {
                    if (editNotes == n.noteId) {
                      return { ...data.data.data }
                    }
                    return n
                  }) || []) as any],
                };
              });
              setEditNotes("")
            },
            onError(error) {
              setEditNotes("")
              toast.error(error.message);
            },
          },
        );
      } else {
        if (!task) {
          await createTaskWithValue();
        }
        useAddNotesMutation.mutate(
          { ...values, tempId: tempIdRef.current },
          {
            onSuccess(data) {
              noteFormik.resetForm();
              toast.success(data.data.message);
              setShowAddField((prev) => !prev)
              setTask((prev) => {
                if (!prev) return prev;

                return {
                  ...prev,
                  taskNotes: [...(prev.taskNotes || []), data.data.data as any],
                };
              });
            },
            onError(error) {
              toast.error(error.message);
              setShowAddField((prev) => !prev)
            },
          },
        );
      }
    },
  });
  const [removeNotes, setRemoveNotes] = useState("");
  const removeNoteMutation = useRemoveNoteMutation();
  const removeNote = () => {
    removeNoteMutation.mutate(removeNotes, {
      onSuccess(data) {
        toast.success(data.data.message);
        setRemoveNotes("");
        setTask((prev) => {
          if (!prev) return prev;

          return {
            ...prev,
            taskNotes:
              prev.taskNotes?.filter((n) => n.noteId !== removeNotes) ?? [],
          };
        });
      },
      onError(error) {
        setRemoveNotes("");
        toast.error(error.message);
      },
    });
  };
  const leftChild = useMemo(
    () => (
      <div
        className={`flex w-full flex-col gap-2 h-full overflow-y-auto @container py-6 px-4`}
      >
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
          {ProjectDetail.data?.data.data.projectName}
        </div>
        {parentTask && (
          <div
            className="flex gap- items-center underline underline-offset-2 text-base text-secondary cursor-pointer"
            onClick={() => setTaskId(task?.parentTaskId ?? "")}
          >
            <img src={BackIcon} className="w-4 h-4" />
            {parentTask}
          </div>
        )}
        <div
          className={`flex items-center justify-between @md:flex-row flex-col @lg:gap-10 gap-2 ${parentTask && "pl-4"
            }`}
        >
          <div className="flex gap-2 items-center w-full">
            {/* ... (Flag and Task Name remain largely the same, adjust widths/gaps if needed at container breakpoints) ... */}
            <div className="w-3.5 h-3.5 min-w-3.5 tooltip">
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {task?.flag == "Green" ? (
                      <img src={GreenFlag} className="w-full" />
                    ) : task?.flag == "Red" ? (
                      <img src={RedFlag} className="w-full" />
                    ) : task?.flag == "Orange" ? (
                      <img src={OrangeFlag} className="w-full" />
                    ) : (
                      ""
                    )}
                  </TooltipTrigger>
                  <TooltipContent className="!p-1.5">
                    {task?.flag == "Green"
                      ? t("ON_TIME")
                      : task?.flag == "Red"
                        ? t("SIGNIFICANT_DELAY")
                        : task?.flag == "Orange"
                          ? t("MODERATE_DELAY")
                          : ""}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center">
              <div>
                {taskNameField || !taskId ? (
                  <div>
                    <InputText
                      onBlur={(e) => {
                        if (e.relatedTarget?.id == "closeTask") return;
                        taskFormik.submitForm();
                        setTaskNameField(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key == "Enter") {
                          taskFormik.submitForm();
                          setTaskNameField(false);
                        }
                      }}
                      name="taskName"
                      onChange={taskFormik.handleChange}
                      onClick={taskFormik.handleBlur}
                      placeholder={t("create_new_task")}
                      value={taskFormik.values.taskName}
                    ></InputText>
                    {taskFormik.values.taskName &&
                      taskFormik.errors.taskName && (
                        <ErrorMessage>
                          {" "}
                          {t(taskFormik.errors.taskName)}
                        </ErrorMessage>
                      )}
                  </div>
                ) : (
                  <div className="@md:text-2xl text-lg font-semibold text-gray-450 max-w-[20ch] truncate">
                    {taskFormik.values.taskName}
                  </div>
                )}
              </div>
              {allowed() && (
                <Button
                  variant={"none"}
                  onClick={() => setTaskNameField((prev) => !prev)}
                >
                  <img src={Edit} className="min-w-3 min-h-3" />
                </Button>
              )}
            </div>
          </div>
          {/* Members / Percentage: Might adjust layout at smaller container widths */}
          {/* Current: inline-flex w-full gap-4 justify-end */}
          {/* If needed, could add e.g., @sm:flex-col to stack in small containers */}
          <div className="inline-flex w-full gap-4 justify-end">
            {/* Percentage circle - typically okay as a fixed size item */}
            {/* {!isNaN(Number(task?.completionPecentage)) && (
              <div>
                <PercentageCircle
                  className="text-sm"
                  percentage={task?.completionPecentage ?? 0}
                  roundColor={`${
                    task?.flag == "Green"
                      ? "#68B266"
                      : task?.flag == "Red"
                      ? "#EA4335"
                      : task?.flag == "Orange"
                      ? "#FFA500"
                      : ""
                  } `}
                />
              </div>
            )} */}
            <div className="">
              <Popover>
                <PopoverTrigger asChild>
                  {/* Disable popover trigger when not allowed or loading, this doesn't need container query */}
                  <div
                    role="button"
                    className={`ml-auto w-fit outline-none ${allowed() ? "cursor-pointer" : "opacity-50 pointer-events-none"}`}
                  >
                    {/* Consider making member avatars section responsive if needed */}
                    <div
                      className={`${member.length > 0 ? "w-36" : "w-36"
                        } flex justify-end items-center`}
                    >
                      {member?.slice(0, 4).map((item, index) => {
                        return (
                          <div
                            key={index}
                            className={`${index > 0 ? "-ml-4" : ""}`} // Adjust overlap with negative margin-left
                          >
                            <UserAvatar
                              className="shadow-sm shadow-gray-300 border-2 border-white"
                              user={item}
                            />
                          </div>
                        );
                      })}
                      {
                        <div
                          className={`flex items-center gap-2 ${!allowed() ? "opacity-50" : ""}  mr-1`}
                        >
                          {" "}
                          {member?.length == 0 && (
                            <div className="text-sm whitespace-nowrap">
                              {t("assign_member")}
                            </div>
                          )}
                          <div className="rounded-full bg-primary-500 flex border border-dashed border-secondary items-center justify-center min-w-8 h-8 w-8 z-10">
                            <img src={NoramlPlus} className="w-3 h-3" />
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                </PopoverTrigger>
                {/* Popover Content layout is already okay */}
                <PopoverContent className="w-fit focus:outline-none z-[9999]">
                  <div className="grid gap-1">
                    <div className="space-y-2 flex gap-2 items-center justify-between">
                      <h4 className="font-medium leading-none">
                        {t("members")}
                      </h4>
                      {!task?.subtasks?.length && !task?.milestoneIndicator && (
                        <div className="text-sm text-gray-400 mr-8">
                          {t("Total work hours for this task")}
                          {": "}
                          <span className="font-semibold">
                            {totalWorkHoursTask}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="w-full justify-end flex items-center">
                      <InputText
                        onChange={(e) => setSearchString(e.target.value)}
                        className="my-2 h-10"
                        placeholder={t("filter_by_name_and_email")}
                      />
                    </div>
                    <div className="max-h-64 overflow-auto">
                      {/* 4. Replace the old mapping logic with the new component */}
                      {filteredUser.map((memberData) => {
                        const assignedInfo = task?.assignedUsers?.find(
                          (u) => u.user.userId === memberData.user.userId,
                        );
                        const isAssigned = !!assignedInfo;

                        return (
                          <MemberAssignmentItem
                            task={task as Task}
                            key={
                              memberData.user.userId +
                              task?.duration +
                              isAssigned
                            }
                            member={memberData}
                            assignedInfo={assignedInfo as any}
                            isAssigned={isAssigned}
                            taskDuration={task?.duration ?? 1}
                            onToggleAssign={handleToggleMember}
                            onUpdateAssign={handleUpdateMemberAssignment}
                          />
                        );
                      })}
                    </div>
                    {
                      <div className="flex w-full justify-end truncate">
                        <Link
                          to={`/members/${props.projectId}`}
                          className="text-xs w-fit  my-3 truncate
 underline text-primary-500  text-right"
                        >
                          {t("Add more members to your project")}
                        </Link>
                      </div>
                    }
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
        {/* Milestone / Progress / Status - Use @container queries */}
        {/* Change md:flex-row to @md:flex-row */}
        <div className="flex flex-col @container @md:flex-row items-center justify-between gap-2.5">
          {/* Milestone/Progress section */}
          <div
            className={`px-2.5 h-9 rounded-md flex justify-between items-center gap-2.5 ${
              // Adjust width based on container breakpoint if needed
              !milestoneFormik.values.milestoneIndicator
                ? "w-full @md:w-full"
                : "w-fit @md:w-fit"
              }`}
          >
            {/* ... (Milestone checkbox and label) ... */}
            <div className="w-full flex items-center gap-2">
              <input
                type="checkbox"
                className="accent-secondary"
                disabled={Boolean(task?.subtasks?.length) || !allowed()}
                checked={milestoneFormik.values.milestoneIndicator}
                onChange={(e) => {
                  milestoneHandlechange(e.target.checked);
                  milestoneFormik.submitForm();
                }}
              />{" "}
              <label className="text-gray-450">{t("milestone")}</label>
            </div>
            {/* Progress / Completed status */}
            <div className="w-full flex justify-end items-center gap-2.5">
              <div className="text-xs text-gray-450">
                {!milestoneFormik.values.milestoneIndicator
                  ? ""
                  : t("Completed")}
              </div>
              {!milestoneFormik.values.milestoneIndicator ? (
                <div className="mr-4">
                  <div className="flex w-full items-center gap-0 tooltip  ">
                    <p className="text-xs text-nowrap ">
                      {t("Actual Progress")}
                    </p>
                    <div className="w-full flex gap-2 items-center justify-end">

                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="w-44 h-fit">
                              {" "}
                              {/* This w-full is relative to its flex item parent */}

                              <Slider
                                key={
                                  taskQuery.isFetching +
                                  "" +
                                  task?.completionPecentage
                                }
                                className="!w-full my-1"
                                defaultValue={[
                                  !task ? 0 : Number(task.completionPecentage),
                                ]}
                                max={100}
                                disabled={
                                  Boolean(task?.subtasks?.length) ||
                                  task?.milestoneIndicator ||
                                  !allowed()
                                }
                                min={0}
                                title={task?.completionPecentage}
                                onValueChange={(e) => {
                                  setSlider(e[0]);
                                }}
                                onValueCommit={(e) => {
                                  onProgressUpdate(e);
                                  setTask((prev) => {
                                    if (!prev) return prev;

                                    return {
                                      ...prev,
                                      completionPecentage: String(e[0] ?? 0),
                                    };
                                  });
                                }}
                              />
                            </div>
                          </TooltipTrigger>

                          <TooltipContent
                            className="z-50 bg-white p-2 rounded-md shadow-md"
                            style={{
                              display:
                                task && task?.subtasks?.length > 0 ? " " : "none",
                            }}
                          >
                            {task && task?.subtasks?.length > 0 && (
                              <p className="text-sm w-72">
                                {t("task_disabled_filed_tool_progress")}
                              </p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <div className="flex gap-2 items-center w-7 justify-end">
                        <div
                          key={task?.completionPecentage}
                          className="text-xs font-medium text-gray-400 flex items-center gap-2"
                        >
                          {slider ? Number(slider).toFixed() + "%" : "0%"}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex w-full items-center gap-1 tooltip ">
                    <p className="text-xs text-nowrap ">
                      {t("Expacted Progress")}
                    </p>
                    <div className="w-full flex gap-2 items-center justify-end">

                      <div className="w-44">
                        {" "}
                        {/* This w-full is relative to its flex item parent */}
                        <Slider
                          key={
                            taskQuery.isFetching +
                            "" +
                            task?.plannedProgression
                          }
                          className="!w-full my-5"
                          defaultValue={[
                            Math.min(isNaN(Number(task?.plannedProgression)) ? 0 : Number(task?.plannedProgression), 100),
                          ]}
                          max={100}
                          disabled={true}
                          min={0}
                        />
                      </div>

                      <div className="flex gap-2 items-center w-7 justify-end">
                        <div
                          key={task?.plannedProgression}
                          className="text-xs font-medium text-gray-400 flex items-center gap-2"
                        >
                          {task?.plannedProgression ? Math.min(Number(task?.plannedProgression), 100).toFixed() + "%" : "0%"}
                        </div>
                      </div>
                    </div></div>
                </div>
              ) : (
                <div className="flex justify-between items-center gap-2.5">
                  {" "}
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="todayDays"
                      checked={task?.status == TaskStatusEnumValue.COMPLETED}
                      className="sr-only peer"
                      disabled={!allowed()}
                      onChange={(e) => {
                        if (e.target.checked) {
                          updateStatus(TaskStatusEnumValue.COMPLETED);
                        } else {
                          updateStatus(TaskStatusEnumValue.NOT_STARTED);
                        }
                      }}
                    />
                    <div
                      className={`${!allowed() ? "opacity-55 cursor-not-allowed" : ""} w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/80 dark:peer-focus:ring-secondary rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-secondary`}
                    ></div>
                  </label>
                </div>
              )}
              {/* Error message positioning might need tweaking */}
              {progressError && (
                <ErrorMessage className="w-1/2">{progressError}</ErrorMessage>
              )}
            </div>
          </div>
          {/* Status Dropdown - width might need adjustment at container breakpoints */}
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                disabled={!allowed() || Boolean(task?.subtasks?.length)}
              >
                <div className="text-sm font-normal cursor-pointer">
                  <Button
                    disabled={!allowed() || Boolean(task?.subtasks?.length)}
                    variant={"secondary"}
                    // Apply w-full by default, let it shrink in the flex container,
                    // or specify responsive width like w-full @md:w-auto
                    className="py-1 h-9 px-3 flex w-full gap-3 justify-start bg-gray-450 text-white hover:bg-gray-450/80"
                  >
                    {task?.status
                      ? `${t("status")}: ${t(task?.status)}`
                      : t("select_status")}
                  </Button>
                </div>
              </DropdownMenuTrigger>
              {/* Dropdown menu content is fine as w-auto */}
              <DropdownMenuContent className="w-auto">
                {taskStatus().map((status) => {
                  return (
                    <div
                      onClick={() => {
                        updateStatus(status);
                      }}
                      className="cursor-pointer flex px-3 w-44 rounded-md hover:bg-slate-50/80 py-2 justify-between items-center"
                    >
                      {t(status)}
                      <CheckIcon
                        className={cn(
                          "ml-auto h-4 w-4",
                          status === task?.status ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </div>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {/* Description - w-full is okay */}
        <div className="w-full ">
          <textarea
            disabled={!allowed()}
            name="taskDescription"
            className="w-full border rounded-md px-2 py-2"
            onBlur={async (e) => {
              if (!taskFormik.errors.taskDescription) {
                if (!taskId && e.target.value) {
                  await createTaskWithValue({
                    taskDescription: e.target.value ?? "",
                  });
                }
                if (e.target.value) {
                  taskFormik.submitForm();
                }
              }
            }}
            onChange={taskFormik.handleChange}
            value={taskFormik.values.taskDescription}
            placeholder={t("Description")}
            cols={30}
            rows={4}
          ></textarea>
          {taskFormik.errors.taskDescription && (
            <ErrorMessage className="w-1/2">
              {" "}
              {/* w-1/2 here might also be relative to container width if parent is flex/grid */}
              {t(taskFormik.errors.taskDescription)}
            </ErrorMessage>
          )}
        </div>
        <div className="w-full">
          <div className="flex gap-2 items-center">

            <div className="flex gap-1 items-center text-sm">
              <img src={Notes} className="w-3.5 min-w-3.5" />
              {t("notes")}
            </div>
            {!showAddField && (
              <Button
                variant={showAddField ? "destructive" : "primary_outline"}
                className="text-xs px-2 py-1 h-fit"
                disabled={!allowed()}
                onClick={() => setShowAddField((prev) => !prev)}
              >
                {t("add")}
              </Button>
            )}
          </div>

          {showAddField && (
            <div className="flex gap-1 items-end justify-between bg-secondary/10 p-2.5 rounded-lg">
              <div className="flex-col items-start w-full">
                <textarea
                  className="w-full border p-2 rounded-lg "
                  value={noteFormik.values.note}
                  rows={2}
                  name="note"
                  onChange={noteFormik.handleChange}
                />
                {noteFormik.errors.note && (
                  <ErrorMessage className="w-1/2">
                    {" "}
                    {/* w-1/2 here might also be relative to container width if parent is flex/grid */}
                    {t(noteFormik.errors.note)}
                  </ErrorMessage>
                )}
              </div>
              <div className="flex items-center gap-1 justify-between ">
                {showAddField && (
                  <Button
                    variant={showAddField ? "destructive" : "primary_outline"}
                    className="text-xs px-2 py-1 h-fit"
                    onClick={() => { setShowAddField((prev) => !prev), noteFormik.resetForm() }}
                  >
                    {t("cancel")}
                  </Button>
                )}
                <Button
                  variant={"primary"}
                  className="text-xs px-2 py-1 h-fit"
                  onClick={() => noteFormik.handleSubmit()}
                >
                  {t("add")}
                </Button>
              </div>
            </div>
          )}

          <div className="mt-2 max-h-64 overflow-y-auto">
            {task?.taskNotes?.map((item) => {
              return (
                <div className="p-4 bg-secondary/10 rounded-lg mb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex gap-1 items-center text-xs text-gray-700 mb-1">
                      {dateFormater(new Date(item.createdAt))}{" "}
                      <span className="text-gray-400">|</span>
                      {` Note added by ${item?.createdByUser?.firstName} ${item?.createdByUser?.lastName}`}
                      {item?.updatedByUser && (
                        <div>
                          {" "}
                          <span className="text-gray-400">|</span>
                          {` Note updated by ${item?.updatedByUser?.firstName} ${item?.updatedByUser?.lastName}`}
                        </div>
                      )}
                    </div>
                    {editNotes !== item.noteId ? (
                      <div className="flex gap-2 items-center">
                        <Button
                          className="!p-0 !h-fit"
                          variant={"none"}
                          onClick={() => {
                            setEditNotes(item.noteId);
                            noteFormik.setFieldValue("note", item.note);
                          }}
                        >
                          <img className="w-3 max-w-3 " src={EditSquare} />
                        </Button>
                        <Button
                          className="!p-0 !h-fit"
                          variant={"none"}
                          onClick={() => {
                            setRemoveNotes(item.noteId);
                          }}
                        >
                          <img className="w-3 max-w-3 " src={TrashCan} />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2 items-center">
                        <Button
                          className="!p-0 !h-fit"
                          variant={"none"}
                          onClick={() => {
                            setEditNotes("");
                            noteFormik.setFieldValue("note", "");
                          }}
                        >
                          <img className="w-4 max-w-4" src={RedCross} />
                        </Button>
                        <Button
                          className="!p-0 !h-fit"
                          variant={"none"}
                          onClick={() => {
                            noteFormik.submitForm();
                          }}
                        >
                          <img className="w-4 max-w-4" src={TickCircle} />
                        </Button>
                      </div>
                    )}
                  </div>
                  {editNotes === item.noteId ? (
                    <div>
                      <textarea
                        className="w-full border p-2 mt-2"
                        value={noteFormik.values.note}
                        rows={2}
                        name="note"
                        onChange={noteFormik.handleChange}
                      />
                      {noteFormik.errors.note && (
                        <ErrorMessage className="w-1/2">
                          {" "}
                          {t(noteFormik.errors.note)}
                        </ErrorMessage>
                      )}
                    </div>
                  ) : (
                    <NoteText text={item.note} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {/* Date / Duration / End Date Grid - Use @container queries */}
        {/* Change md:grid-cols-3 and grid-cols-1 to container breakpoints */}
        {/* Add a breakpoint for 2 columns like @sm:grid-cols-2 if desired */}
        <div className="grid grid-cols-1 @container @md:grid-cols-2 @lg:grid-cols-3 gap-5">
          <div className="mt-2 w-full">
            {" "}
            {/* w-full is fine inside grid item */}
            <div className="flex items-center gap-1">
              <img src={CalendarIcon} alt="" className="w-4.5 min-w-4.5 mb-1" />
              <label className="text-sm font-normal !text-black">
                {" "}
                {t("start_date")}
              </label>
              <span className="text-red-500 text-sm">*</span>
            </div>
            {(task?.subtasks?.length == 0 || !task) && (
              <Popover>
                <PopoverTrigger asChild>
                  <div className="tooltip w-full">
                    <Button
                      type="button"
                      variant={"none"}
                      className="border px-2 py-3 h-10 rounded-md w-full"
                      disabled={
                        !allowed() || (task && task?.subtasks?.length > 0)
                      }
                    >
                      <div className="flex w-full gap-3 justify-start">
                        <div className="text-gray-800 text-sm">
                          {dateFormater(
                            new Date(taskFormik.values.startDate ?? startDate),
                          )}
                        </div>
                      </div>
                    </Button>
                  </div>
                </PopoverTrigger>
                {/* Popover content calendar is fine */}
                <PopoverContent
                  className="p-0 z-[9999]"
                  onCloseAutoFocus={() =>
                    taskId ? taskFormik.submitForm() : ""
                  }
                >
                  <Calendar
                    mode="single"
                    fromYear={2020}
                    toYear={2300}
                    captionLayout="dropdown-buttons"
                    selected={getSelectedDate()}
                    onSelect={async (e) => {
                      {
                        const startDate = UTCDateConverter(new Date(e ?? ""));
                        if (!taskId) {
                          await createTaskWithValue({
                            startDate: startDate,
                          });
                        }
                        taskFormik.setFieldValue("startDate", startDate);
                        setUpdateDate(startDate);
                      }
                    }}
                    className="rounded-md border"
                    defaultMonth={UTCDateConverter(
                      task?.startDate ? new Date(task?.startDate) : new Date(),
                    )}
                    disabled={[
                      {
                        dayOfWeek: nonWorkingDays(),
                      },
                      {
                        before: ProjectDetail?.data?.data?.data?.startDate
                          ? new Date(ProjectDetail.data.data.data.startDate)
                          : startDate,
                      },
                      ...(holiday ?? []),
                    ]}
                  />
                  {taskFormik.errors.startDate &&
                    taskFormik.values.startDate && (
                      <ErrorMessage className="ml-0 p-0"></ErrorMessage>
                    )}
                </PopoverContent>
              </Popover>
            )}
            {task && task?.subtasks?.length > 0 && (
              <div className="tooltip">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger
                      asChild
                      className="border px-2 py-3 h-10 rounded-md w-full opacity-50"
                    >
                      <div className="flex w-full gap-1 justify-start">
                        <div className="text-gray-400 text-sm">
                          {dateFormater(
                            new Date(taskFormik.values.startDate ?? startDate),
                          )}
                        </div>
                        <div className="ml-auto">
                          <img
                            src={InfoCircle}
                            className="h-[16px] w-[16px]"
                            alt="InfoCircle"
                          />
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {task && task?.subtasks?.length > 0 && (
                        <p className="text-sm w-72">
                          {" "}
                          {t("task_disabled_filed_tool")}
                        </p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
          <div className="mt-2" key={taskId}>
            {" "}
            {/* w-full is fine inside grid item */}
            <div className="flex items-center gap-1 tooltip">
              <label className="text-sm font-normal !text-black">
                {" "}
                {t("Duration")}:
              </label>
              <span className="text-red-500 text-sm">*</span>
              {task?.subtasks?.length == 0 && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <img
                        src={InfoCircle}
                        className="h-[16px] w-[16px]"
                        alt="InfoCircle"
                      />
                    </TooltipTrigger>
                    <TooltipContent
                      className="absolute top-2"
                      align="center"
                      side="top"
                    >
                      <p className="text-sm w-72 text-wrap">
                        {t("duration_description")}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {(task?.subtasks?.length == 0 || !task) && (
              <div className="flex flex-col w-full">
                {!taskDurationField && (
                  <div className="flex gap-5">
                    <Button
                      variant={"none"}
                      onClick={() =>
                        task?.subtasks?.length == 0 || !task
                          ? setTaskDurationField((prev) => !prev)
                          : ""
                      }
                      disabled={task?.milestoneIndicator || !allowed()}
                      className="w-full flex justify-between border px-2 py-3 h-10 rounded-md"
                    >
                      <div className="text-xs font-medium text-gray-400 flex gap-2 ">
                        <div className="text-sm  text-gray-700">
                          {taskFormik.values.duration ?? 0.0} {t("Day")}
                        </div>
                      </div>
                    </Button>
                  </div>
                )}
                {taskDurationField && (
                  <div className="flex items-center gap-2.5 w-full ">
                    {" "}
                    {/* w-full is fine here */}
                    <div className="w-full">
                      <InputNumber
                        onBlur={async (e) => {
                          const newDuration = Number(e.target.value);
                          if (!newDuration || taskFormik.errors.duration) {
                            setTaskDurationField(false);
                            return;
                          }
                          if (Number(newDuration) === Number(task?.duration ?? 0)) {
                            setTaskDurationField(false);
                            return;
                          }
                          if (!taskId) {
                            await createTaskWithValue({
                              duration: newDuration,
                            });
                          } else {
                            taskUpdateMutation.mutate(
                              { duration: newDuration },
                              {
                                onSuccess() {
                                  refetch();
                                },
                                onError(error) {
                                  if (
                                    error.response?.data.message?.includes(
                                      "Would you like to remove the dependency?",
                                    )
                                  ) {
                                    setErrorMesage(error.response?.data.message);
                                    setInvalidDate(true);
                                    refetch();
                                  } else {
                                    refetch();
                                    toast.error(
                                      error.response?.data.message == "Unauthorized"
                                        ? t(
                                            "You are not authorized to edit tasks which are not assigned to you",
                                          )
                                        : errorKeyFormate(
                                            error.response?.data.message + "",
                                          ),
                                      errorConfig,
                                    );
                                  }
                                },
                              },
                            );
                          }
                          setTaskDurationField(false);
                        }}
                        autoFocus
                        name="duration"
                        max={TASK_MAX_DURATION_DAYS}
                        onChange={(e) => {
                          taskFormik.setFieldValue("duration", e.target.value ? Number(e.target.value) : 0);
                        }}
                        onClick={taskFormik.handleBlur}
                        value={taskFormik.values.duration}
                        className="!h-10 !w-full"
                      ></InputNumber>
                      {taskFormik.errors.duration && (
                        <ErrorMessage className="w-1/2">
                          {" "}
                          {t(taskFormik.errors.duration)}
                        </ErrorMessage>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {task && task?.subtasks?.length > 0 && (
              <div className="tooltip">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex gap-5 opacity-50">
                        <div className="w-full flex justify-between border px-1.5 py-2 h-8 rounded-md">
                          <div className="text-xs font-medium text-gray-400 flex gap-2 ">
                            <div className="text-sm  text-gray-300">
                              {taskFormik.values.duration ?? 0.0} Day
                            </div>
                          </div>

                          <div className="flex items-center justify-end relative">
                            <img
                              src={InfoCircle}
                              className="h-[16px] w-[16px]"
                              alt="InfoCircle"
                            />
                          </div>
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {task && task?.subtasks?.length > 0 && (
                        <p className="text-sm w-72 ">
                          {t("task_disabled_filed_tool")}
                        </p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
          <div className="mt-2">
            {" "}
            {/* w-full is fine inside grid item */}
            {/* <Button
              variant={"none"}
              className="border px-1.5 py-2 h-8 rounded-md w-full opacity-50"
              disabled={!allowed()}
            >
              <div className="flex w-full gap-3 justify-start">
                <img src={CalendarIcon} className="w-3.5" />
                <div>
                  {t("")}
                  <span className="text-red-500 text-sm">*</span>
                </div>
                <div className="text-gray-400 text-sm">
                  {tasks?.endDate
                    ? dateFormater(new Date(tasks.endDate))
                    : ""}
                </div>
              </div>
            </Button> */}
            <div className="flex items-center gap-1">
              <img src={CalendarIcon} alt="" className="w-4.5 min-w-4.5 mb-1" />
              <label className="text-sm font-normal !text-black">
                {" "}
                {t("end_date")}
              </label>
              <span className="text-red-500 text-sm">*</span>
            </div>
            {(task?.subtasks?.length == 0 || !task) && (
              <Popover>
                <PopoverTrigger asChild>
                  <div className="tooltip w-full">
                    <Button
                      type="button"
                      variant={"none"}
                      className="border px-2 py-3 h-10 rounded-md w-full"
                      disabled={
                        !allowed() || (task && task?.subtasks?.length > 0)
                      }
                    >
                      <div className="flex w-full gap-3 justify-start">
                        <div className="text-gray-00 text-sm">
                          {taskId
                            ? dateFormater(endDate ?? getSelectedDate())
                            : dateFormater(
                              new Date(
                                new Date(
                                  taskFormik.values.startDate ??
                                  getSelectedDate(),
                                ).setDate(
                                  new Date(
                                    taskFormik.values.startDate ??
                                    getSelectedDate(),
                                  ).getDate() +
                                  Number(taskFormik.values.duration),
                                ),
                              ),
                            )}
                        </div>
                      </div>
                    </Button>
                  </div>
                </PopoverTrigger>
                <PopoverContent
                  className="p-0 z-[9999]"
                  onCloseAutoFocus={() =>
                    taskId ? taskFormik.submitForm() : ""
                  }
                  side="bottom"
                >
                  <Calendar
                    fromYear={2020}
                    toYear={2300}
                    captionLayout="dropdown-buttons"
                    mode="single"
                    selected={endDate ?? getSelectedDate()}
                    defaultMonth={UTCDateConverter(
                      task?.endDate ? new Date(task?.endDate) : new Date(),
                    )}
                    onSelect={async (e) => {
                      {
                        if (e) {
                          const startDate = UTCDateConverter(
                            new Date(taskFormik.values.startDate),
                          );
                          const endDate = UTCDateConverter(new Date(e));
                          const duration = calculateDurations(
                            startDate,
                            endDate,
                          );
                          setEndDate(e);
                          if (!taskId) {
                            await createTaskWithValue({
                              duration: duration,
                            });
                          }
                          taskFormik.setFieldValue("duration", duration);
                        } else {
                          if (!taskId) {
                            await createTaskWithValue({
                              duration: 1,
                            });
                          }
                        }
                      }
                    }}
                    className="rounded-md border h-full"
                    disabled={[
                      {
                        dayOfWeek: nonWorkingDays(),
                      },
                      {
                        before: new Date(taskFormik.values.startDate),
                      },
                      ...(holiday ?? []),
                    ]}
                  />
                  {/* {taskFormik.errors.startDate &&
                    taskFormik.values.startDate && (
                      <ErrorMessage className="ml-0 p-0"></ErrorMessage>
                    )} */}
                </PopoverContent>
              </Popover>
            )}
            {task && task?.subtasks?.length > 0 && (
              <div className="tooltip">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger
                      asChild
                      className="border px-1.5 py-2 h-8 rounded-md w-full opacity-50"
                    >
                      <div className="flex w-full gap-1 justify-start">
                        <div className="text-gray-400 text-sm">
                          {dateFormater(new Date(endDate ?? getSelectedDate()))}
                        </div>
                        <div className="ml-auto">
                          <img
                            src={InfoCircle}
                            className="h-[16px] w-[16px]"
                            alt="InfoCircle"
                          />
                        </div>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {task && task?.subtasks?.length > 0 && (
                        <p className="text-sm w-72">
                          {" "}
                          {t("task_disabled_filed_tool")}
                        </p>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        </div>

        {/* Sub-tasks and Attachments Grid - Use @container queries */}
        {/* Change md:grid-cols-2 to @md:grid-cols-2 */}
        <div className="grid grid-cols-1 @container @md:grid-cols-2 gap-x-3 gap-y-0">
          <div className="">
            {" "}
            {/* w-full is fine in grid item */}
            <div
              className={` flex items-center justify-between gap-2.5  px-1.5 py-2 h-8 rounded-md ${task?.milestoneIndicator && "opacity-75 bg-gray-400/5"
                }`}
            >
              {!subTaskFieldShow && (
                <div className="flex items-center">
                  <img src={ParentTaskIcon} className="w-4 h-4 mr-1" />
                  <div className="text-sm font-medium text-nowrap">
                    {t("Sub-tasks")}
                  </div>
                </div>
              )}
              <div className="w-full flex items-center">
                {subTaskFieldShow && (
                  <>
                    <div className="relative flex w-full  border border-gray-300 rounded-md px-2 py-0">
                      <img
                        src={ParentTaskIcon}
                        className="w-4 h-4 absolute left-1 top-1/2 -translate-y-1/2"
                      />
                      <div className="relative w-full">
                        <InputText
                          name="taskName"
                          placeholder="What needs to be done?"
                          onChange={handleInputChange}
                          value={subTask}
                          autoFocus
                          onKeyDown={(e) => {
                            e.key === "Enter" && subTask?.length < 250
                              ? createSubTask()
                              : "";
                          }}
                          className="!h-8 w-5/6 ml-4 mt-0 pl-0 border-none relative rounded-none border-opacity-75 focus:!outline-none placeholder:text-sm"
                        />

                        {showDropdown && filteredTasks?.length > 0 && (
                          <ul className="absolute z-10 w-5/6 bg-white border border-gray-300 border-t-0 max-h-48 overflow-y-auto shadow-md">
                            {createSubtasksOption().map((task, idx) => (
                              <li
                                key={idx}
                                className="px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm"
                                onClick={() => handleSelectTask(task)}
                              >
                                {task.taskName}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <Button
                        variant={"none"}
                        onClick={() => {
                          (setSubTaskFieldShow(false), setSubtask(""));
                        }}
                        className="p-0 absolute right-7 top-1/2 -translate-y-1/2"
                      >
                        <img
                          src={Close}
                          className="w-3.5 h-3.5"
                          width={20}
                          height={20}
                        />
                      </Button>
                      <Button
                        variant={"none"}
                        onClick={createSubTask}
                        className="p-0 absolute right-2 top-1/2 -translate-y-1/2"
                        disabled={Boolean(!subTask) || subTask?.length > 250}
                      >
                        <img
                          src={PlusSvg}
                          className="w-4 h-4"
                          width={20}
                          height={20}
                        />
                      </Button>
                    </div>
                  </>
                )}
              </div>
              {!subTaskFieldShow && allowed() && (
                <Button
                  variant={"none"}
                  className="h-fit w-fit p-0"
                  disabled={task?.milestoneIndicator}
                  onClick={() => {
                    setSubTaskFieldShow((prev) => (prev = !prev));
                  }}
                >
                  <img
                    src={PlusSvg}
                    className="min-w-4 min-h-4"
                    width={20}
                    height={20}
                  />
                </Button>
              )}
            </div>
            {subTask?.length > 250 && (
              <ErrorMessage>{t("character limit exceeded")}</ErrorMessage>
            )}
            <div className="max-h-52 mt-3 overflow-auto">
              {task &&
                task.subtasks
                  ?.slice(0, showAllSubTitle ? task.subtasks?.length : 5)
                  .map((task, index) => {
                    return (
                      <div
                        className="w-full flex justify-between bg-[#FAFAFB] items-center lex mt-2 px-1.5 py-3 text-sm rounded-md !bg-primary-50"
                        key={index}
                      >
                        {!showConfirmEdit ||
                          showConfirmEdit.taskId !== task.taskId ? (
                          <div className="w-full truncate cursor-pointer">
                            <div className="flex gap-2 w-full">
                              <p
                                className="text-sm font-normal underline truncate w-fit  text-left"
                                onClick={() => setTaskId(task.taskId)}
                              >
                                {task.taskName}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full flex flex-col ">
                            <InputText
                              className="mt-0 h-8 w-5/6 rounded-md"
                              defaultValue={
                                showConfirmEdit
                                  ? (showConfirmEdit as any).taskName
                                  : ""
                              }
                              onChange={(e) => {
                                setShowConfirmEdit((prev) => {
                                  if (!prev) return prev;
                                  return { ...prev, taskName: e.target.value };
                                });
                              }}
                            />
                            {showConfirmEdit.taskId &&
                              !showConfirmEdit.taskName && (
                                <span className="mt-2 text-xs text-red-500">
                                  {t("Required*")}
                                </span>
                              )}
                          </div>
                        )}
                        {allowed() &&
                          (!showConfirmEdit ||
                            showConfirmEdit.taskId !== task.taskId ? (
                            <div className="flex gap-2 items-center">
                              <img
                                src={EditSquare}
                                className="cursor-pointer w-4 min-w-4"
                                onClick={() =>
                                  setShowConfirmEdit({
                                    taskId: task.taskId,
                                    taskName: task.taskName,
                                  })
                                }
                              />
                              <img
                                src={TrashCan}
                                className="cursor-pointer w-4 min-w-4"
                                onClick={() =>
                                  setShowConfirmDelete(task.taskId)
                                }
                              />
                            </div>
                          ) : (
                            <div className="flex gap-2 items-center">
                              <Button
                                variant={"none"}
                                disabled={!showConfirmEdit.taskName}
                                onClick={() => {
                                  if (guardProjectEditBlocked()) return;
                                  taskUpdateMutation.mutate(
                                    {
                                      id: showConfirmEdit.taskId,
                                      taskName: showConfirmEdit.taskName,
                                    },
                                    {
                                      onSuccess() {
                                        refetch();
                                        setShowConfirmEdit(null);
                                      },

                                      onError(error) {
                                        if (
                                          error.response?.data.message?.includes(
                                            "Would you like to remove the dependency?",
                                          )
                                        ) {
                                          setErrorMesage(
                                            error.response?.data.message,
                                          );
                                          setInvalidDate(true);
                                          refetch();
                                        } else {
                                          refetch();
                                          toast.error(
                                            error.response?.data.message ==
                                              "Unauthorized"
                                              ? t(
                                                "You are not authorized to edit tasks which are not assigned to you",
                                              )
                                              : errorKeyFormate(
                                                error.response?.data.message +
                                                "",
                                              ),
                                            errorConfig,
                                          );
                                        }
                                      },
                                    },
                                  );
                                }}
                              >
                                <img
                                  src={TickCircle}
                                  className="cursor-pointer w-4 min-w-4"
                                />
                              </Button>

                              <img
                                src={RedCross}
                                className="cursor-pointer w-4 min-w-4"
                                onClick={() => setShowConfirmEdit(null)}
                              />
                            </div>
                          ))}
                      </div>
                    );
                  })}
              {task && task?.subtasks?.length && task?.subtasks?.length > 5 ? (
                <div
                  className="text-end text-xs mt-2 cursor-pointer text-primary-500"
                  onClick={() => setShowAllSubTitle((prev) => !prev)}
                >
                  {!showAllSubTitle ? t("view_more") : ""}
                </div>
              ) : (
                ""
              )}
            </div>
          </div>

          {/* Task Dependencies - Use @container query for col-span */}
          {/* Change md:col-span-2 to @md:col-span-2 */}
          <div className="">
            {" "}
            <div className="flex justify-between px-1.5 py-2 h-8  items-center">
              <div className="flex items-center ">
                <img src={PapperClip} className="w-4 h-4 mr-1" />
                <div className="text-sm font-medium text-nowrap flex items-center gap-2">
                  {t("attachment")}
                  {attachmentUploading && (
                    <div>
                      <Loader2Icon
                        className="animate-spin w-4"
                        color="#808080"
                      />
                    </div>
                  )}
                </div>
              </div>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="none"
                    className="ml-auto p-0 h-fit w-fit cursor-pointer"
                    disabled={attachmentUploading || !allowed()}
                  >
                    {allowed() && (
                      <img
                        src={PlusSvg}
                        className="w-4 h-4 hover:opacity-85 transition-opacity"
                        width={20}
                        height={20}
                      />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="focus:outline-none w-64 p-1.5 z-[9999]">
                  {showLinkInput ? (
                    <div className="flex flex-col gap-2 p-2">
                      <h4 className="font-medium text-sm leading-none mb-1">
                        {t("add_link")}
                      </h4>
                      <InputText
                        name="linkName"
                        placeholder={t("link_name")}
                        value={linkName}
                        onChange={(e) => setLinkName(e.target.value)}
                        className="h-8"
                      />
                      <div className="flex gap-4 my-1">
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <input
                            type="radio"
                            value="WEB"
                            checked={linkType === "WEB"}
                            onChange={() => setLinkType("WEB")}
                          />
                          {t("web_url")}
                        </label>

                        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                          <input
                            type="radio"
                            value="LOCAL"
                            checked={linkType === "LOCAL"}
                            onChange={() => setLinkType("LOCAL")}
                          />
                          {t("local_network")}
                        </label>
                      </div>
                      <InputText
                        name="linkUrl"
                        placeholder={
                          linkType === "WEB"
                            ? "https://example.com"
                            : "\\\\UPLOADS\\file.pdf"
                        }
                        value={linkUrl}
                        onChange={(e) => setLinkUrl(e.target.value)}
                        className="h-8"
                      />
                      {linkUrl && linkType === "WEB" && !isWebUrl(linkUrl) && (
                        <span className="text-red-500 text-xs">
                          {t("please_enter_valid_url_like_https_or_http")}
                        </span>
                      )}

                      {linkUrl &&
                        linkType === "LOCAL" &&
                        !isLocalPath(linkUrl) && (
                          <span className="text-red-500 text-xs">
                            {t("please_enter_valid_local_path")}
                          </span>
                        )}
                      <div className="flex justify-end gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowLinkInput(false);
                            setLinkName("");
                            setLinkUrl("");
                          }}
                          className="h-8"
                        >
                          {t("back")}
                        </Button>
                        <Button
                          className="h-8"
                          size="sm"
                          onClick={() => {
                            addLink(
                              { name: linkName, url: linkUrl },
                              {
                                onSuccess: () => {
                                  setShowLinkInput(false);
                                  setLinkName("");
                                  setLinkUrl("");
                                  setOpen(false);
                                  refetch();
                                },
                              },
                            );
                          }}
                          disabled={
                            !linkName ||
                            !linkUrl ||
                            isLinkUploading ||
                            (linkType === "WEB" && !isWebUrl(linkUrl)) ||
                            (linkType === "LOCAL" && !isLocalPath(linkUrl))
                          }
                          isLoading={isLinkUploading}
                        >
                          {t("add")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      <Button
                        variant={"none"}
                        className="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-700 hover:bg-slate-100/70 hover:text-primary rounded-md transition-colors font-medium justify-start cursor-pointer"
                        onClick={() => fileInput.current?.click()}
                        isLoading={attachmentUploading}
                        disabled={attachmentUploading || !allowed()}
                      >
                        <img src={DesktopSvg} className="w-4 h-4" />
                        {t("From computer")}
                      </Button>
                      <Button
                        variant={"none"}
                        className="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-700 hover:bg-slate-100/70 hover:text-primary rounded-md transition-colors font-medium justify-start cursor-pointer"
                        onClick={openPicker}
                      >
                        <img src={DropboxSvg} className="w-4 h-4" />
                        {t("From dropbox")}
                      </Button>
                      <Button
                        variant={"none"}
                        className="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-700 hover:bg-slate-100/70 hover:text-primary rounded-md transition-colors font-medium justify-start cursor-pointer"
                        onClick={openGoogleDrivePicker}
                      >
                        <img src={GoogleSvg} className="w-4 h-4" />
                        {t("from_google_drive")}
                      </Button>
                      <Button
                        variant={"none"}
                        className="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-700 hover:bg-slate-100/70 hover:text-primary rounded-md transition-colors font-medium justify-start cursor-pointer"
                        onClick={() => setShowLinkInput(true)}
                      >
                        <img src={PapperClip} className="w-4 h-4" />
                        {t("from_link")}
                      </Button>
                    </div>
                  )}
                </PopoverContent>
              </Popover>
            </div>
            {/* w-full is fine in grid item */}
            <input
              type="file"
              ref={fileInput}
              onChange={(e) => fileUpload(e)}
              className="hidden"
              multiple
            />
            {task?.documentAttachments &&
              task?.documentAttachments?.length > 0 ? (
              <TaskAttachment
                refetch={refetch}
                task={task}
                allowed={allowed()}
              ></TaskAttachment>
            ) : (
              ""
            )}
          </div>
          <div className="w-full col-span-2 ">
            {" "}
            {/* w-full is fine, col-span-1 is default */}
            <TaskDependencies
              projectId={props.projectId}
              task={task}
              refetch={refetch}
              allowed={allowed()}
              setTaskId={(id: string) => setTaskId(id)}
              setTask={(task: Task) => setTask(task)}
              allTask={allTask.data?.data.data ?? []}
            />
          </div>
        </div>
      </div>
    ),
    [
      // Keep all dependencies as they are
      allTask.data?.data.data,
      allowed,
      attachmentUploading,
      calculateDurations,
      createSubTask,
      createTaskWithValue,
      endDate,
      fileUpload,
      getSelectedDate,
      handleToggleMember,
      holiday,
      member,
      milestoneFormik,
      milestoneHandlechange,
      nonWorkingDays,
      onProgressUpdate,
      parentTask,
      progressError,
      props.projectId,
      ProjectDetail.data?.data.data.projectName,
      refetch,
      showAllSubTitle,
      slider,
      startDate,
      subTask,
      t,
      task,
      taskAddMembersMutation.isPending,
      taskDurationField,
      taskFormik,
      taskId,
      taskNameField,
      taskQuery.isFetching,
      taskRemoveMembersMutation.isPending,
      taskStatus,
      updateStatus,
      open,
      showLinkInput,
      linkName,
      linkUrl,
      linkType,
      isLinkUploading,
      addLink,
      subTaskFieldShow,
      showDropdown,
      filteredTasks,
      handleSelectTask,
      createSubtasksOption,
      showAddField,
      noteFormik,
      editNotes,
      removeNotes,
      filteredUser,
      setSearchString,
      totalWorkHoursTask,
      handleUpdateMemberAssignment,
    ],
  );
  const rightChild = useMemo(
    () => (
      <div className="relative h-full w-full flex flex-col bg-[#F1F2FE] px-4 py-4 overflow-hidden">
        {/* Header */}
        <div className="flex justify-between shrink-0">
          <div className="flex items-center gap-2.5 mt-4">
            <img src={ScaleSvg} className="w-4 h-4" />
            <div className="text-2xl font-light text-[#171A1F]">
              {t("activity")}
            </div>
          </div>
          <div className="absolute right-4 top-4 z-50">
            <Button variant={"none"} id="closeTask" onClick={close}>
              <img src={Close} className="h-4 w-4 min-w-4" />
            </Button>
          </div>
        </div>

        <hr className="w-11/12 mt-3 mx-auto shrink-0" />

        <div className="flex flex-col h-full min-h-0 gap-4">
          <div className="flex flex-col max-h-1/2 min-h-0">
            <TaskComment
              projectId={props.projectId}
              setTask={setTask}
              setTaskId={setTaskId}
              task={task}
              refetch={refetch}
              allowed={allowed()}
            />
          </div>

          <div className="flex flex-col mt-12 max-h-1/2 min-h-0">
            <TaskHistory task={task} />
          </div>
        </div>
      </div>
    ),
    [allowed, close, props.projectId, refetch, t, task],
  );

  return (
    <Dialog
      isOpen
      onClose={() => {
        taskId ? allTask.refetch() : close();
      }}
      modalClass="w-fit h-fit md:w-[85%] md:max-h-[90%] md:min-h-[80%] flex  bg-white rounded-md "
      key={taskId}
    >
      {(taskQuery.isFetching ||
        taskQuery.isRefetching ||
        taskCreateMutation.isPaused) && (
          <Loader className="absolute top-0 left-0  z-50" />
        )}

      <div className="md:hidden flex justify-end">
        <Button variant={"none"} onClick={close} className="ml-auto">
          <img src={Close} className="h-4 w-4 " />
        </Button>
      </div>
      <div className="flex gap-2 md:flex-row flex-col w-full  !min-h-full relative">
        <OverlaySlider
          initialOverlayWidth="25%"
          sliderWidth={4}
          sliderColor="#6b728044"
          overlayBgColor="white"
          overlayShadow="none"
          zIndexBase={50}
        >
          {leftChild}
          {rightChild}
        </OverlaySlider>

        {invalidDate && errorMessage && (
          <Dialog
            isOpen={invalidDate}
            onClose={() => { }}
            modalClass="rounded-lg"
          >
            <div className="flex flex-col gap-2 p-6 ">
              <img src={TrashCan} className="w-12 m-auto" /> {errorMessage}
              <div className="flex gap-2 ml-auto">
                <Button
                  variant={"outline"}
                  onClick={() => setInvalidDate(false)}
                  isLoading={removeTaskDependenciesMutation.isPending}
                  disabled={removeTaskDependenciesMutation.isPending}
                >
                  {t("cancel")}
                </Button>
                <Button
                  variant={"primary"}
                  onClick={() => {
                    setInvalidDate(false);
                    deleteDependenciesId();
                  }}
                >
                  {t("delete")}
                </Button>
              </div>
            </div>
          </Dialog>
        )}
        <Dialog
          isOpen={scheduledError}
          onClose={() => { }}
          modalClass="rounded-lg w-1/3"
        >
          <div className="flex flex-col gap-2 p-6 ">
            <img src={YallowInfoIcon} className="w-12 m-auto" />{" "}
            <div dangerouslySetInnerHTML={{ __html: t("futureTaskWarning") }} />
            <div className="flex gap-2 ml-auto">
              <Button
                variant={"primary"}
                onClick={() => {
                  setScheduledError(false);
                  setTask((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      completionPecentage: String(
                        taskQuery.data?.data.data.completionPecentage,
                      ),
                    };
                  });
                }}
              >
                {t("ok")}
              </Button>
            </div>
          </div>
        </Dialog>
        <Dialog
          isOpen={Boolean(showConfirmDelete)}
          onClose={() => { }}
          modalClass="rounded-lg"
        >
          <div className="flex flex-col gap-2 p-6 ">
            <img src={TrashCan} className="w-12 m-auto" />{" "}
            {t("common_delete_message")}
            <div className="flex gap-2 ml-auto">
              <Button
                variant={"outline"}
                isLoading={removeTaskMutation.isPending}
                disabled={removeTaskMutation.isPending}
                onClick={() => setShowConfirmDelete(null)}
              >
                {t("cancel")}
              </Button>
              <Button
                variant={"primary"}
                onClick={removeTask}
                isLoading={removeTaskMutation.isPending}
                disabled={removeTaskMutation.isPending}
              >
                {t("delete")}
              </Button>
            </div>
          </div>
        </Dialog>
        <Dialog
          isOpen={Boolean(removeNotes)}
          onClose={() => { }}
          modalClass="rounded-lg"
        >
          <div className="flex flex-col gap-2 p-6 ">
            <img src={TrashCan} className="w-12 m-auto" />{" "}
            {t("common_delete_message")}
            <div className="flex gap-2 ml-auto">
              <Button
                variant={"outline"}
                isLoading={removeNoteMutation.isPending}
                disabled={removeNoteMutation.isPending}
                onClick={() => setRemoveNotes("")}
              >
                {t("cancel")}
              </Button>
              <Button
                variant={"primary"}
                onClick={removeNote}
                isLoading={removeNoteMutation.isPending}
                disabled={removeNoteMutation.isPending}
              >
                {t("delete")}
              </Button>
            </div>
          </div>
        </Dialog>

        <ProjectStatusEditBlockedDialog
          isOpen={isProjectEditBlockedDialogOpen}
          onClose={() => {
            setIsProjectEditBlockedDialogOpen(false);
            props.close("");
          }}
          projectStatus={projectStatus}
          projectSettingsLink={`/project-details/${props.projectId}`}
          modalClass="rounded-lg w-3/5 text-center"
        />
      </div>
    </Dialog>
  );
}

export default memo(TaskSubTaskForm);

const NoteText = ({ text }: { text: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [text]);

  return (
    <div>
      <div
        ref={ref}
        className="text-sm overflow-hidden"
        style={
          expanded
            ? {}
            : {
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }
        }
      >
        {text}
      </div>

      {isTruncated && (
        <button
          className="text-xs text-blue-500 mt-1"
          onClick={() => setExpanded((p) => !p)}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};
